/**
 * FE-19: Feature Value Tab -- Feature adoption, exposure, repeat usage, retention lift.
 * DatePresets + BarDistribution (exposure vs adoption) + collapsible feature table.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchFeatureValueMetrics } from "../../api/admin-metrics";
import type {
  FeatureValueMetrics,
  FeatureValueRow,
} from "../../api/admin-metrics";
import { Td, Th, tableStyles } from "../../components/DataTable";
import { DatePresets } from "../../components/DatePresets";
import { BarDistribution } from "../../components/charts/BarDistribution";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import { pct } from "./helpers";

type SortField = "retention_lift" | "adoption";

function defaultRange(): { start_date: string; end_date: string } {
  const end = new Date().toISOString().slice(0, 10);
  const start = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  return { start_date: start, end_date: end };
}

export function FeatureValueTab(): React.JSX.Element {
  const [range, setRange] = useState(defaultRange);
  const { data, error, loading, load } = useFeatureValueData();
  const [sortBy, setSortBy] = useState<SortField>("retention_lift");
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    void load(range.start_date, range.end_date);
  }, [load, range]);

  const sortedRows = data ? sortRows(data.rows, sortBy) : [];

  const barData = useMemo(() => {
    if (!data) return [];
    return data.rows.map((row) => ({
      name: row.feature_name,
      value: row.adoption * 100,
    }));
  }, [data]);

  return (
    <div>
      <DatePresets value={range} onChange={setRange} />
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && !data && (
        <p style={shared.loadingText}>{t("common.loading")}</p>
      )}
      {barData.length > 0 && (
        <section style={shared.section}>
          <h3 style={shared.sectionTitle}>{t("feature_value.col_adoption")}</h3>
          <BarDistribution data={barData} color="#16a34a" />
        </section>
      )}
      <section style={shared.section}>
        <button
          type="button"
          onClick={() => setShowTable(!showTable)}
          style={s.toggleBtn}
        >
          {showTable ? t("common.hide_details") : t("common.show_details")}
        </button>
        {showTable && data && (
          <FeatureTable rows={sortedRows} sortBy={sortBy} onSort={setSortBy} />
        )}
      </section>
    </div>
  );
}

// --- Feature Table ---

function FeatureTable({
  rows,
  sortBy,
  onSort,
}: {
  rows: ReadonlyArray<FeatureValueRow>;
  sortBy: SortField;
  onSort: (field: SortField) => void;
}): React.JSX.Element {
  return (
    <div style={{ marginTop: 12 }}>
      <h3 style={shared.sectionTitle}>{t("feature_value.title")}</h3>
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <Th>{t("feature_value.col_feature")}</Th>
            <Th>{t("feature_value.col_exposure")}</Th>
            <Th>
              <SortButton
                label={t("feature_value.col_adoption")}
                field="adoption"
                active={sortBy}
                onSort={onSort}
              />
            </Th>
            <Th>{t("feature_value.col_repeat")}</Th>
            <Th>
              <SortButton
                label={t("feature_value.col_retention_lift")}
                field="retention_lift"
                active={sortBy}
                onSort={onSort}
              />
            </Th>
            <Th>{t("feature_value.col_status")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.feature_name}>
              <Td>{row.feature_name}</Td>
              <Td mono>{pct(row.exposure)}</Td>
              <Td mono>{pct(row.adoption)}</Td>
              <Td mono>{pct(row.repeat)}</Td>
              <Td mono>{pct(row.retention_lift)}</Td>
              <Td>
                <StatusBadge row={row} />
              </Td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <Td colSpan={6}>{t("common.no_data_for_period")}</Td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// --- Sort Button ---

function SortButton({
  label,
  field,
  active,
  onSort,
}: {
  label: string;
  field: SortField;
  active: SortField;
  onSort: (f: SortField) => void;
}): React.JSX.Element {
  const isActive = field === active;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      style={isActive ? s.sortBtnActive : s.sortBtn}
    >
      {label} {isActive ? "v" : ""}
    </button>
  );
}

// --- Status Badge ---

function StatusBadge({ row }: { row: FeatureValueRow }): React.JSX.Element {
  const status = deriveStatus(row);
  return (
    <span style={{ ...shared.badge, background: status.color }}>
      {status.label}
    </span>
  );
}

// --- Helpers ---

type FeatureStatus = { readonly label: string; readonly color: string };

function deriveStatus(row: FeatureValueRow): FeatureStatus {
  if (row.repeat > 0.3) {
    return { label: t("feature_value.core_feature"), color: "#16a34a" };
  }
  if (row.exposure > 0.3 && row.adoption < 0.05) {
    return { label: t("feature_value.exposed_but_weak"), color: "#dc2626" };
  }
  if (row.adoption > 0 && row.repeat < 0.05) {
    return { label: t("feature_value.used_but_shallow"), color: "#ca8a04" };
  }
  return { label: t("feature_value.neutral"), color: "#6b7280" };
}

function sortRows(
  rows: ReadonlyArray<FeatureValueRow>,
  field: SortField,
): ReadonlyArray<FeatureValueRow> {
  return [...rows].sort((a, b) => b[field] - a[field]);
}

// --- Hooks ---

function useFeatureValueData(): {
  data: FeatureValueMetrics | null;
  error: string | null;
  loading: boolean;
  load: (start: string, end: string) => Promise<void>;
} {
  const [data, setData] = useState<FeatureValueMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (start: string, end: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        setData(
          await fetchFeatureValueMetrics({
            start_date: start,
            end_date: end,
          }),
        );
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err.message : t("common.failed_to_load"),
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, error, loading, load };
}

// --- Styles ---

const s: Record<string, React.CSSProperties> = {
  toggleBtn: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
    cursor: "pointer",
  },
  sortBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    padding: 0,
  },
  sortBtnActive: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--color-primary)",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    padding: 0,
  },
};
